
import React, { useMemo, useState, useEffect } from 'react';
import { format } from 'date-fns/format';
import { startOfMonth } from 'date-fns/startOfMonth';
import { endOfMonth } from 'date-fns/endOfMonth';
import { startOfWeek } from 'date-fns/startOfWeek';
import { endOfWeek } from 'date-fns/endOfWeek';
import { eachDayOfInterval } from 'date-fns/eachDayOfInterval';
import { isSameMonth } from 'date-fns/isSameMonth';
import { isSameDay } from 'date-fns/isSameDay';
import { isToday } from 'date-fns/isToday';
import { addDays } from 'date-fns/addDays';
import { getDay } from 'date-fns/getDay';
import { cn } from '@/lib/utils';
import { Class } from '@/types/classTypes';
import { CalendarEvent } from './CalendarEvent';
import { useClassEnrollments } from '@/hooks/useClassEnrollments';

interface CustomMonthViewProps {
  selectedDate: Date;
  selectedMonth: Date;
  eventsByDate: Record<string, { classId: string; classData: Class }[]>;
  onDateSelect: (date: Date) => void;
  onEventSelect: (classData: Class) => void;
  viewType: 'month' | 'week';
  isFullscreen?: boolean;
}

export const CustomMonthView: React.FC<CustomMonthViewProps> = ({
  selectedDate,
  selectedMonth,
  eventsByDate,
  onDateSelect,
  onEventSelect,
  viewType,
  isFullscreen = false
}) => {
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Track window width for responsive behavior
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 640);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);
  
  // Extract all unique class IDs from events
  const allClassIds = useMemo(() => {
    const classIds = new Set<string>();
    Object.values(eventsByDate).forEach(events => {
      events.forEach(event => classIds.add(event.classId));
    });
    return Array.from(classIds);
  }, [eventsByDate]);
  
  // Fetch enrollment counts for all classes
  const { data: enrollmentCounts = {} } = useClassEnrollments(allClassIds);
  
  // Generate days for the month view
  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [selectedMonth]);
  
  // Generate days for the week view
  const weekDays = useMemo(() => {
    // If we're in week view, show the current week
    // otherwise, show the week containing the selected date
    const viewDate = viewType === 'week' ? new Date() : selectedDate;
    const weekStart = startOfWeek(viewDate);
    
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [selectedDate, viewType]);
  
  // Determine which days to display based on the view type
  const daysToDisplay = viewType === 'month' ? monthDays : weekDays;

  return (
    <div 
      className={cn(
        "calendar-container flex flex-col",
        isFullscreen 
          ? "h-full w-full bg-white" 
          : "p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-sm"
      )}
      style={isFullscreen ? { height: '100%', overflow: 'hidden' } : {}}
    >
      {/* Calendar header - weekday names */}
      <div className={cn(
        "grid grid-cols-7 bg-white shrink-0",
        isFullscreen 
          ? "border-b border-gray-300" 
          : "rounded-lg shadow-sm border mb-2"
      )}>
        {WEEKDAYS.map((day) => (
          <div 
            key={day} 
            className={cn(
              "text-center font-bold text-slate-700 uppercase tracking-wider",
              isFullscreen 
                ? "py-2 sm:py-3 text-sm sm:text-lg border-r last:border-r-0 border-gray-300 bg-gray-100" 
                : "py-4 text-sm border-r last:border-r-0 border-gray-200"
            )}
          >
            {isFullscreen && isMobile ? day.slice(0, 1) : day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid - FULL HEIGHT with enhanced overflow handling */}
      <div 
        className={cn(
          "grid grid-cols-7 flex-1",
          isFullscreen 
            ? "gap-0 overflow-hidden" 
            : "gap-3 auto-rows-fr"
        )}
        style={isFullscreen ? { 
          height: 'calc(100% - 60px)', 
          minHeight: 0,
          overflow: 'hidden'
        } : {}}
      >
        {daysToDisplay.map((day) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const eventsForDay = eventsByDate[dateKey] || [];
          const isSelected = isSameDay(day, selectedDate);
          const isTodayDate = isToday(day);
          const isCurrentMonth = isSameMonth(day, selectedMonth);
          
          return (
            <div
              key={dateKey}
              className={cn(
                "relative flex flex-col transition-all duration-200 cursor-pointer",
                isFullscreen 
                  ? "border-r border-b border-gray-300 p-1 sm:p-2 md:p-4 h-full min-h-[100px] sm:min-h-[120px] md:min-h-[160px] overflow-hidden" 
                  : "p-4 rounded-lg border shadow-sm min-h-[180px]",
                isCurrentMonth ? "bg-white" : "bg-gray-50/80",
                isSelected ? 
                  (isFullscreen ? "bg-blue-50" : "border-blue-400 shadow-lg ring-2 ring-blue-100") 
                  : "",
                isTodayDate ? 
                  (isFullscreen ? "bg-blue-100/30" : "border-blue-500 bg-blue-50/30 shadow-md") 
                  : "",
                isFullscreen 
                  ? "hover:bg-gray-50" 
                  : "hover:bg-white hover:shadow-lg hover:border-gray-300 hover:scale-[1.02]",
                // Remove right border for last column (every 7th item)
                isFullscreen && ((daysToDisplay.indexOf(day) + 1) % 7 === 0) ? "!border-r-0" : "",
                // Remove bottom border for last row
                isFullscreen && (daysToDisplay.indexOf(day) >= daysToDisplay.length - 7) ? "!border-b-0" : ""
              )}
              onClick={() => onDateSelect(day)}
            >
              <div className={cn(
                "font-bold self-start transition-colors",
                isFullscreen 
                  ? "text-sm sm:text-lg md:text-xl px-1 sm:px-2 py-1 mb-1 sm:mb-2" 
                  : "text-lg px-3 py-2 rounded-full mb-3",
                isTodayDate ? 
                  (isFullscreen ? "text-blue-700" : "bg-blue-600 text-white shadow-lg") 
                  : isSelected ? 
                  (isFullscreen ? "text-blue-600" : "bg-blue-100 text-blue-700") 
                  : "text-slate-800"
              )}>
                {format(day, 'd')}
              </div>
              
              <div className={cn(
                "flex-1 overflow-y-auto custom-scrollbar pr-1",
                isFullscreen ? "space-y-1" : "space-y-3"
              )}>
                {eventsForDay.slice(0, isFullscreen ? (isMobile ? 2 : 4) : 3).map((event, idx) => (
                  <CalendarEvent 
                    key={`${event.classId}-${idx}`}
                    classData={event.classData}
                    enrolledCount={enrollmentCounts[event.classId]}
                    isFullscreen={isFullscreen}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventSelect(event.classData);
                    }}
                  />
                ))}
              </div>
              
              {eventsForDay.length > (isFullscreen ? (isMobile ? 2 : 4) : 3) && (
                <div className={cn(
                  "text-center text-blue-600 font-semibold bg-blue-50 cursor-pointer transition-colors hover:bg-blue-100 touch-manipulation",
                  isFullscreen 
                    ? "text-xs mt-1 py-1 px-2 rounded min-h-[24px] flex items-center justify-center" 
                    : "text-sm mt-3 py-2 rounded-lg"
                )}>
                  +{eventsForDay.length - (isFullscreen ? (isMobile ? 2 : 4) : 3)} more
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Custom scrollbar styles - enhanced implementation */}
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background-color: rgba(148, 163, 184, 0.4);
            border-radius: 20px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background-color: rgba(148, 163, 184, 0.6);
          }
        `}
      </style>
    </div>
  );
};
